import type { CustomDimensions } from '@de/types';
import {
  isOutboundUrlDimensionKey,
  sanitizeOutboundUrlForTelemetry,
} from './sanitizeOutboundUrlForTelemetry';

const stringify = (value: unknown): string => {
  if (typeof value === 'object') return JSON.stringify(value);

  return String(value);
};

/**
 * Tagular's ingestion endpoint rejects `customDimensions` payloads whose
 * values aren't strings (HTTP 400 — observed by analytics on the legacy
 * affiliate-bundle).
 *
 * Input must be the canonical `{ key, value }[]` shape that
 * `DynamicExperienceProvider` (and other DE callers) already use — not a raw
 * object map. Values are typed as `string` on `EventContext`, but each entry
 * is still coerced at runtime so stray non-strings cannot reach Tagular.
 *
 * Sets an empty string for undefined or null values
 */
export const normalizeCustomDimensions = (
  customDimensions?: CustomDimensions | null,
): CustomDimensions | undefined => {
  if (customDimensions === undefined || customDimensions === null) {
    return undefined;
  }

  const result = customDimensions.flatMap(({ key, value }) => {
    if (value === undefined || value === null) {
      return [];
    }
    const stringValue = stringify(value);
    if (isOutboundUrlDimensionKey(key)) {
      const sanitized = sanitizeOutboundUrlForTelemetry(stringValue);
      if (sanitized == null || sanitized === '') {
        return [];
      }
      return [{ key, value: sanitized }];
    }
    return [{ key, value: stringValue }];
  });

  return result?.length > 0 ? result : undefined;
};
